/**
 * Small helpers + sub-components lifted out of ``contributions-list.tsx``
 * (#2211) so the main file stays under the 300-line limit. Nothing here
 * has its own state; every helper is a pure function or a stateless
 * presentation component.
 */

import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Badge } from '@components/atoms/ui-kit/badge.tsx';
import { Text } from '@components/atoms/ui-kit/text.tsx';
import {
  CustomModelDataStatus,
  ModelData
} from '@utils/api/custom-models/use-get-custom-models-data.tsx';
import { PATHS } from '../../router/paths.ts';

/** Session link when the source is still there; a muted "no longer
 *  available" line when retention has aged it out. That is normal — the
 *  clip itself is fine and still trains.
 *
 *  The link uses the ROUTABLE string ids (`source_session_zoom_id`,
 *  `source_document_uuid`) — not the integer FKs. The router matches on
 *  the string `zoom_id` (e.g. `9ebe4dab0000520000cd64804c`) and the
 *  document UUID; using the integer PKs produced dead URLs like
 *  `/captions/70788/3826`. Falls through to the "session only" line if
 *  the backend hasn't returned the string ids (e.g. a stale API deploy
 *  or a document with a FK that was preserved but the join failed). */
export const ContributionSource = ({ clip }: { clip: ModelData }) => {
  const { t } = useTranslation();

  if (!clip.source_session_id) {
    return (
      <Text as="span" size="xs" tone="muted" className="italic">
        {t('contribution-source-unavailable')}
      </Text>
    );
  }
  if (clip.source_session_zoom_id && clip.source_document_uuid) {
    return (
      <Link
        to={
          PATHS(clip.source_session_zoom_id, clip.source_document_uuid).CAPTIONS
        }
        className="text-xs underline decoration-dotted underline-offset-2"
        style={{ color: 'inherit' }}
      >
        {t('contribution-source-open')}
      </Link>
    );
  }
  return (
    <Text as="span" size="xs" tone="muted">
      {t('contribution-source-session', { id: clip.source_session_id })}
    </Text>
  );
};

export const ContributionStatusBadge = ({
  status
}: {
  status?: CustomModelDataStatus;
}) => {
  const { t } = useTranslation();
  const resolved = status ?? CustomModelDataStatus.READY;
  if (resolved === CustomModelDataStatus.PROCESSING) {
    return (
      <Badge color="sky">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
        <span className="ml-1">{t('processing')}</span>
      </Badge>
    );
  }
  if (resolved === CustomModelDataStatus.FAILED) {
    return <Badge color="red">{t('failed')}</Badge>;
  }
  return <Badge color="green">{t('ready')}</Badge>;
};

export const isPendingContribution = (
  clip: ModelData,
  lastCompletedRound: number | null
): boolean => {
  if (clip.round_number == null) {
    // No round has claimed this clip yet. If there is a completed round on
    // the model, this contribution is genuinely pending; if there is not,
    // "pending" would be noise on every clip so keep it plain.
    return lastCompletedRound != null && lastCompletedRound > 0;
  }
  if (lastCompletedRound == null) return false;
  return clip.round_number > lastCompletedRound;
};

export const formatClipDuration = (seconds: number): string => {
  if (!seconds || seconds < 0) return '';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return s === 0 ? `${m}m` : `${m}m ${s}s`;
};

/** Format seconds as ``m:ss.mmm`` so the contribution row shows the
 *  same shape of timecode the customer sees on the transcript timeline.
 *  Used with `start_time` + `end_time` (both in seconds) to render the
 *  clip's window inside its source document. */
const formatClipTimecode = (seconds: number): string => {
  const totalMs = Math.max(0, Math.round(seconds * 1000));
  const m = Math.floor(totalMs / 60000);
  const s = Math.floor((totalMs % 60000) / 1000);
  const ms = totalMs % 1000;
  return `${m}:${String(s).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
};

export const formatClipTimeRange = (start: number, end: number): string => {
  return `${formatClipTimecode(start)} → ${formatClipTimecode(end)}`;
};

/** Strip the ``.m4a`` / ``.vtt`` suffix so the audio + transcript rows
 *  that back the same contribution collide onto one stem. Contributions
 *  are always named ``{stem}.m4a`` and ``{stem}.vtt`` in the training-
 *  clips extractor (see api-scriptix/.../training_clips.py). */
export const stemOf = (name: string): string => name.replace(/\.[^./]+$/, '');

/** Small label/value pair used in the contribution row's meta line so
 *  numbers like ``0:15 → 0:18`` and ``3s`` are not floating without
 *  context. The label is a very-muted micro-caps prefix. */
export const LabelledStat = ({
  label,
  value,
  title,
  tabularNums
}: {
  label: string;
  value: string;
  title?: string;
  tabularNums?: boolean;
}) => (
  <span
    className="inline-flex items-baseline gap-1"
    title={title}
    style={{ whiteSpace: 'nowrap' }}
  >
    <Text
      as="span"
      size="xs"
      tone="muted"
      className="uppercase tracking-wider text-[10px] opacity-70"
    >
      {label}
    </Text>
    <Text
      as="span"
      size="xs"
      tone="muted"
      className={tabularNums ? 'tabular-nums' : undefined}
    >
      {value}
    </Text>
  </span>
);
